/**
 * Simulation V3 — the module's OWN translation bundle (moved out of
 * `src/_translations/{en,he}/translations.js`, 2026-08-04).
 **
 * The screen is an isolated, lazily-loaded module (architecture D-2), so
 * its strings live with it instead of in the app-wide files: one place to
 * add a label, one place to translate it, and nothing to merge against
 * the shared bundles. `registerSimulationV3Translations()` merges these
 * into the running i18next instance under the SAME `simulationV3.*`
 * namespace the components already call, so no `t()` key changes.
 *
 * Hebrew reuses the app's existing vocabulary (מקבץ, סניף, מהדורה,
 * תגיות, מדפים…). Languages without a bundle here fall back to English
 * through i18next's `fallbackLng`, exactly as before.
 *
 * Wording follows the client's "Simulation V3 Translations — FINAL"
 * review (sheets `simulationV3` + `globalKeys`, 2026-08-11): the glossary
 * (cluster = מקבץ, field = שדה, facing = פייס, pegboard = לוח תלייה,
 * clipboard = לוח גזירים, canvas = משטח העבודה, rule = כלל) and the style
 * rules (masculine imperative, no אנא, ביטול/אישור, full spelling הכול).
 */

import { STALENESS_TRANSLATIONS } from './staleness.bundle'
import i18n from 'i18next'

export const SIMULATION_V3_TRANSLATIONS = {
  en: {
    simulationV3: {
      productDetails: {
        catalogImportName: 'Catalog Import Name',
        productBrand: 'Product Brand',
        unitOfMeasure: 'Unit of Measure',
        centimeters: 'Centimeters',
        millimeters: 'Millimeters',
        inches: 'Inches',
        sensitivity: 'Sensitivity',
        hangable: 'Hanging',
        isHangable: 'Hangable',
        horizontal: 'Horizontal',
        vertical: 'Vertical',
        enablePriceTag: 'Enable Price Tag',
        internalCode: 'Internal Code',
        image: 'Image',
        dropzone: 'Drag and drop files here, or click to select files',
        sourceDetails: 'Source Details',
        isMandatory: 'Mandatory',
        isStackable: 'Stackable',
        isBlock: 'Block',
        mvpOverride: 'MVP/On-Top Override',
        minRevenue: 'Use min. revenue in',
        gs1Override: 'GS1 Override',
        archive: 'Archive',
        rerunToRefresh:
          'The product left master data. Re-run the simulation to refresh the layout.',
        imageTypeRejected: 'Choose a PNG, JPG, WEBP or AVIF image.',
        imageTooLarge: 'Image is larger than 5MB.',
        maxStackCount: 'Max Stack Count',
        archiveConfirm: 'Are you sure you want to archive this product?',
        maxStackCountHint:
          "Leave this field blank or 0 if you don't want to limit stackable count",
        archivedToast: '{{name}} archived successfully.',
        restoredToast: '{{name}} restored successfully.',
      },
      screenTitle: 'Simulation',
      screenTitleOf: 'of',
      dev: {
        loadSample: 'Load sample data (dev)',
        load4Field: 'Load 4-field sample (dev)',
        load50Field: 'Load 50-field stress sample (dev)',
      },
      /* CR010 — the "color by" control, its list and the tint slider. */
      colorBy: {
        title: 'Color by',
        none: 'None',
        tint: 'Tint opacity',
        dim: {
          brand: 'Brand',
          supplier: 'Supplier',
          subCategory: 'Sub category',
          category: 'Category',
        },
      },
      toolbar: {
        tools: 'Tools',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        zoomReset: 'Reset view',
        pan: 'Pan',
        xray: 'X-ray',
        fullscreen: 'Full Screen',
        exitFullscreen: 'Exit Full Screen',
        // AUI only — V2's `MINIMIZE_Screen`, the `minimize-2` button
        // that closes the full-screen EDIT modal back to the panel.
        minimize: 'Minimize screen',
        undo: 'Undo',
        redo: 'Redo',
        viewStackables: 'View stackables',
        hideStackables: 'Hide stackables',
        dropToPegboard: 'Drop to Pegboard',
        dropToShelf: 'Drop to Shelf',
        duEdit: 'Edit display units',
        iconLegend: 'Icon Legend',
      },
      errors: {
        loadFailed: 'Failed to load the simulation. Please try again.',
        saveFailed: 'Failed to save the simulation. Please try again.',
        clustersFailed: 'Failed to load clusters.',
        ackWarningFailed: 'Failed to remove the warning. Please try again.',
        // Story 4.5: REFRESH_FAILED_KEY has pointed here since story 3.1 but
        // the key never existed in either tree — that alert rendered its own
        // raw key string. It fires when the refresh cannot even be STARTED
        // (no loaded session / no usable edition id), so nothing was applied.
        refreshFailed: 'Could not start the refresh. Please try again.',
      },
      clipboard: {
        title: 'Clipboard',
        removed: 'Removed',
        added: 'Added',
        unknownProduct: 'Unknown product',
        empty: 'Clipboard is empty',
        clearAll: 'Clear All',
        clearSelection: 'Clear Selection',
        singleDrag: 'Single group drag',
        multiDrag: 'Multiple group drag',
        discard: 'Remove from clipboard',
        discardSingle:
          'This will remove it from the clipboard after you save the planogram. Continue?',
        discardSelected:
          'This will remove the selected items from the clipboard after you save the planogram. Continue?',
        discardAll: 'This will clear the whole clipboard. Continue?',
      },
      dataPanel: {
        searchPlaceholder: 'Search here…',
        chooseColumns: 'Choose up to 3 columns',
        cancel: 'Cancel',
        confirm: 'Confirm',
        recentlyAdded: 'Recently added',
        allCategories: 'All Categories',
        allSuppliers: 'All Suppliers',
        weighted: 'Weighted share',
        allocated: 'Allocated share',
        shareOfSale: 'Share of sale',
        salesUnits: 'Units',
        dailyRate: 'Daily sales rate',
        byFace: 'By Facings',
        hangable: 'Hangable/Non Hangable',
        hangableOnly: 'Hangable',
        nonHangableOnly: 'Non Hangable',
        tolerance: 'Tolerance',
        share: 'Share',
        saveAndPrintPrompt:
          'Your last changes are not saved. Print after the save completes?',
      },
      contextMenu: {
        facings: 'Facings',
        stack: 'Stack',
        apply: 'Apply',
        close: 'Close',
        expand: 'Expand',
      },
      copyArrangement: {
        blocked:
          'The selected simulation has a different field structure — cannot copy',
        failed: 'Copy arrangement failed',
        shelfMismatch:
          'The shelf structure differs from the current layout. Continue anyway?',
        empty: 'No matching simulations found',
        editionsEmpty: 'No editions found',
        searching: 'Searching simulations…',
        applying: 'Copying the arrangement…',
        mismatchTitle: 'Structure mismatch',
        continue: 'Continue',
        sessionLost:
          'The simulation changed while the arrangement was being copied — nothing was copied. Please try again.',
        filterTitle: 'Copy Arrangement Filter',
        cluster: 'Cluster',
        search: 'Search',
        fieldsWidth: 'Fields Width',
        planogramContains: 'Planogram that contains…',
      },
      filter: {
        cluster: 'Cluster',
        selectCluster: 'Select cluster…',
        branch: 'Branch',
        selectBranch: 'Select Branch',
        clusterLevel: 'Cluster level',
        tags: 'Tags',
        selectTags: 'Select tags',
        strategy: 'Strategy',
        edition: 'Edition',
        fieldCount: 'Fields',
        defaultCount: 'Default',
        simulate: 'Simulate',
        simulating: 'Simulating…',
        save: 'Save',
        saving: 'Saving…',
        pendingFeature: 'Coming soon in the revamp',
        customBranchInfo:
          'In custom mode, this branch selection is used for simulation push purposes only',
        fieldArrayMismatch:
          'The saved simulation was built on a different field array than the one selected. Review before editing.',
      },
      tabs: {
        preview: 'Preview',
        summary: 'Summary',
      },
      panel: {
        emptyList: 'No items yet',
        pendingBackend: 'Available in an upcoming update.',
        dimensionChanged: 'Product dimensions changed since simulation.',
        filters: 'Filters',
        listView: 'List view',
        gridView: 'Grid view',
        copyBarcode: 'Click to copy barcode',
      },
      /* CR011: panel ↔ canvas locate (double-click). */
      locate: {
        notPlaced: 'The product is not placed on the planogram',
        notInPanel:
          'The product is not shown in the data panel under the current filter',
        rowTooltip: 'Double-click to locate on the canvas',
        duCardTooltip:
          'Click for the product card; double-click to locate on the planogram',
      },
      stackable: {
        /**
         * i18next v19 plurals (client review, 2026-08-11: "'1 מוצרים' with
         * count=1"). English resolves to the BASE key at n=1 and `_plural`
         * otherwise; Hebrew uses the four index suffixes this version emits
         * — `_0` = 1, `_1` = 2, `_2` = tens, `_3` = everything else. The
         * base key stays as the fallback for any suffix that is absent.
         */
        marked: '{{count}} product marked as stackable.',
        marked_plural: '{{count}} products marked as stackable.',
        cleared: 'Stackable turned off for {{count}} product.',
        cleared_plural: 'Stackable turned off for {{count}} products.',
        allEmpty: 'There are no products on the canvas to mark.',
        failed: 'Could not update the product(s) — the change was rolled back.',
      },
      pegboard: {
        drawHint: 'Draw a rectangle on the canvas to place the pegboard.',
        cancel: 'Cancel',
        deleteMovedToClipboard:
          'Pegboard deleted. {{count}} hung product moved to the clipboard.',
        deleteMovedToClipboard_plural:
          'Pegboard deleted. {{count}} hung products moved to the clipboard.',
        updateTitle: 'Update Peg Board?',
        updateMovesToClipboard:
          'This peg board contains hanging products. Updating it will move all hanging products to the clipboard. Do you want to continue?',
        updateCancel: 'Cancel',
        updateConfirm: 'Yes, Update',
        deleteTitle: 'Delete Peg Board?',
        deleteMessage:
          'This peg board contains hanging products. Deleting it will move all hanging products to the clipboard. Do you want to continue?',
        deleteCancel: 'Cancel',
        deleteConfirm: 'Yes, Delete',
      },
      dropDisplace: {
        title: 'Not enough space',
        message:
          'There is no room to shift the products aside on this level. Move the following product(s) to the clipboard and drop the new product in their place?',
        confirm: 'Move to clipboard & drop',
        cancel: 'Cancel',
      },
      export: {
        button: 'Export',
        pdfFull: 'PDF — Full planogram',
        pdfFieldwise: 'PDF — Field by field',
        excelFieldwise: 'Excel — Field by field',
        cover: 'Building cover page…',
        capturing: 'Rendering planogram…',
        excelProducts: 'Excel — Product list',
        preparing: 'Preparing export…',
        collecting: 'Collecting data…',
        writing: 'Writing file…',
        done: 'Done',
        failed: 'Export failed. Please try again.',
        unsavedPrompt: 'You have unsaved changes. Save before exporting?',
        configureColumns: 'Configure Excel columns',
        saveAndExport: 'Save & export',
        fieldPlanogram: 'Field planogram',
        fieldWisePrint: 'Field Wise Print',
        categoryWisePrint: 'Category Wise Print',
        facing: 'Facing',
        stackCount: 'Stack Count',
      },
      addProduct: {
        title: 'Add products',
        added: 'Added',
      },
      duEdit: {
        hint: 'Pick a unit on the board to edit, or add a new one',
        title: 'Display Units',
        shelvesTab: 'Shelves',
        pickShelf: 'Pick a Shelve to start editing',
        close: 'Close',
        addShelf: '+ Shelf',
        removeShelf: '− Shelf',
        removeBlocked: 'Cannot remove a shelf with products on it.',
        movedToClipboard:
          'Products without full shelf support were moved to the clipboard.',
        unsavedTitle: 'Unsaved Changes',
        unsavedMessage: 'You have unsaved changes. Leave anyway?',
        unsavedBack: 'Back',
        unsavedLeave: 'Yes',
        deleteShelfTitle: 'Delete Shelf Confirmation',
        deleteShelfMessage:
          'There is {{count}} product placed on the shelf. Should it be moved to the clipboard?',
        deleteShelfMessage_plural:
          'There are {{count}} products placed on the shelf. Should they be moved to the clipboard?',
        deleteShelfBack: 'Back',
        deleteShelfConfirm: 'Yes',
        shelfTitle: 'Shelf : S{{rank}}',
        space: 'Shelf Space',
        fromFloor: 'From the Floor',
        thickness: 'Thickness',
        addAbove: '+ Above',
        addBelow: '+ Below',
        overflowWarning: 'Product Overflow Warning',
      },
      /**
       * Confirm dialogs. Wording tracks V2's `SIMULATE_SAVE_CONFIRM`,
       * `UNSAVED_CHANGES*` and `DU_*` keys so the two screens read the
       * same — see `_translations/{en,he}/translations.js`.
       */
      modal: {
        saveTitle: 'Save simulate category changes',
        saveMessage: 'Are you sure to save this simulate category changes?',
        unsavedTitle: 'Unsaved Changes',
        discard: 'Discard',
        saveAndContinue: 'Save & Continue',
        resetAndContinue: 'Reset & Continue',
        arrangeTitle: 'Display Unit Selection for Re-Arrangement',
        arrangeMessage:
          'Do you want to use the current edited Display Unit or reset to the selected Cluster Field Array?',
        useCurrentDU: 'Use Current Display Unit',
        resetFieldArray: 'Reset to Selected Field Array',
        closeTitle: 'Are you sure to close simulation?',
      },
      rules: {
        categoryOverlap: 'Category areas overlap',
        ruleExceeded: 'Sub-category rule exceeded',
        field: 'Field',
        none: 'No rule violations.',
      },
      warnings: {
        overlap: 'The product overlaps another product.',
        hungOverlap:
          'The product — or a layer of its stack — runs into a product hanging on the pegboard above.',
        pegOverlap: 'The hung product overlaps another product.',
        boardArea:
          'That position is inside the pegboard area — there is no room for this product under the board.',
        noFreeHole:
          'No free hole at that position on the pegboard. Try a spot with more clear space.',
        pegGridUnsaved:
          'Save the pegboard values before placing products on it.',
        heightExceedsShelf: 'The product is taller than the shelf allows.',
        heightExceedsShelfDetail:
          'The product does not fit on this shelf: “{{name}}” is {{height}} cm tall, but the shelf has only {{space}} cm of height.',
        heightExceedsShelfSet:
          '{{count}} products in this set are too tall for this shelf. “{{name}}” is {{height}} cm tall, but the shelf has only {{space}} cm of height.',
        maxStackExceeded:
          "The stack count exceeds this product's maximum stack count of {{max}}. Enter a value of {{max}} or less.",
        orientationNotStackable:
          'This product is not stackable in its current display option.',
        mixedMove: "Shelf and hung products can't be moved together.",
        mixedClipboard:
          "Can't cut, copy or delete shelf and hung products together.",
        noRoomToExpand: 'No room to expand facings on either side.',
        pegboardOverlap: 'The hung product would collide with the shelves.',
        noSpace: 'No space available at the target position.',
        dropNotEnoughSpace:
          'Cannot drop the product here: there is not enough space on this shelf, and the products already there cannot be shifted aside to make room.',
        dropLongGroupMiddle:
          'This product group is long. Drop the product near its first 2 or last 2 products.',
        setPartialPaste:
          'There is room here for only {{placed}} of the {{total}} products in this set. A set goes back whole.',
        setNoSpace: 'No space available for the dropped product set.',
        setCannotPlace: 'The product set cannot be placed here: {{reason}}',
        hungMembersKept:
          '{{count}} products in this set were hanging on a pegboard, so they stay in the clipboard — drop them on a board to put them back.',
        hungSetNeedsBoard:
          'Every product in this set was hanging on a pegboard. Drop the set on a board to put them back.',
        notSameRow:
          'That shelf is not on the same row as the products being moved. Drop them on a shelf level with their own.',
        outsideField: 'The product was dropped outside any field.',
        ruleViolation: 'This placement violates a category rule.',
        takeBackDisabled: 'Take-back is disabled.',
        duEditBlocked: "This shelf can't be edited while a pegboard covers it.",
        dropConfirmation: 'Please confirm this product drop.',
        dimensionsChanged:
          'Product dimensions changed since the catalog entry.',
        notHangable: 'Only hangable products can be placed on a pegboard.',
      },
      // Reported 2026-09-10: this whole namespace had no `he` (or centralized
      // `en`) entries anywhere — every string fell back to its own inline
      // `defaultValue`, so a Hebrew session always showed English text.
      // `src/_translations/en/translations.js` carries a near-duplicate of
      // this English content, added by an earlier story directly to the
      // legacy file with its own comment flagging Hebrew coverage as an
      // undecided product question — left untouched (not this module's
      // load path going forward) rather than deleted, in case anything
      // else still resolves through it.
      //
      // 2026-10-20: 17 keys that lived ONLY in that legacy file (and so had
      // no Hebrew anywhere — `preview.*`, `anomaly.*`, `image.pending`, eight
      // `toast.*`, `dialog.addMode.nameRequired`, `flyout.title` and three
      // `excelImport.*`) were copied in here alongside their client-approved
      // Hebrew. This bundle registers last with `deep`+`overwrite`, so those
      // legacy entries are now fully shadowed; they are still not deleted,
      // for the same reason given above. Add new keys HERE, not there — a
      // key that exists only in the legacy file can never have Hebrew.
      displayOptions: {
        banner:
          "You're editing the catalog — changes apply immediately to every planogram.",
        loading: 'Loading…',
        header: {
          barcodeLabel: 'barcode',
        },
        orientation: {
          front: 'Front',
          frontLaydown: 'Front Laydown',
          side: 'Side',
          sideLaydown: 'Side Laydown',
          top: 'Top',
          topRotated: 'Top Rotated',
        },
        list: {
          orientations: 'Orientations',
          displayModes: 'Display Modes',
          orientationsHint: 'automatic',
          displayModesHint: 'manual',
          noModes: 'No display modes yet',
          addMode: '+ Add display mode',
          default: 'Default',
          archived: 'Archived',
          disabled: 'Disabled',
          disabledTag: 'disabled',
          capacity: 'cap. {{units}}',
          orientationLocked: 'Orientations cannot be archived or deleted',
          orientationLockedDisabled:
            'Disabled — orientations cannot be archived or deleted',
        },
        preview: {
          banner:
            'These six orientations are created automatically when you save the product. You can configure them afterwards.',
          detailHint:
            'Save the product to configure dimensions, stacking, hanging and images for each option.',
        },
        detail: {
          empty: 'Select an option to view details',
          name: 'Name',
          width: 'Width',
          height: 'Height',
          depth: 'Depth',
          uom: 'UOM',
          capacity: 'Capacity (units per pack)',
          calculated: 'calculated',
          stackable: 'Stackable',
          maxStackableCount: 'Max stack count',
          maxStackableCountHint:
            "Leave this field blank or 0 if you don't want to limit stackable count",
          hangable: 'Hangable',
          hangHoleX: 'Horizontal',
          hangHoleY: 'Vertical',
          /* fix-082 follow-up (user, 2026-10-28): "if type 0 show like this
             warning", pointing at Capacity's "Must be a whole number greater
             than 0." The legacy screens answer a 0 hole with "Add Hanging
             Horizontal", which names the field rather than the problem.
             "Whole number" is deliberately NOT carried over: a hole is a
             position, and 16.5 / 0.5 are the values this app itself seeds. */
          hangHolePositive: 'Must be greater than 0.',
          yes: 'Yes',
          no: 'No',
          update: 'Update',
          updated: 'Updated',
          setDefault: 'Set as default',
          enable: 'Enable',
          disable: 'Disable',
          archive: 'Archive',
          delete: 'Delete',
          lockDefaultDisable: 'Cannot disable the default option',
          lockArchivedToggle:
            'Cannot change enabled state of an archived option',
          lockDefaultArchive: 'Cannot archive the default option',
          lockDefaultDelete:
            'Cannot delete the default option — set another option as default first',
          lockOrientationArchive:
            'Orientations cannot be archived — use disable',
          lockSetDefault: 'Cannot set a disabled or archived option as default',
        },
        image: {
          label: 'Image',
          readOnly: 'Image upload is not available',
          pending: 'Will be uploaded when you save',
        },
        affectedInstances: {
          title: 'This affects live placements',
          body: 'This changes the {{name}} in the catalog. {{count}} live placement will update immediately — this cannot be undone.',
          body_plural:
            'This changes the {{name}} in the catalog. {{count}} live placements will update immediately — this cannot be undone.',
          ok: 'Got it',
        },
        toast: {
          needsEvict:
            'This change would need to move other products to the clipboard — resolve it from Manage display options.',
          catalogEvicted:
            '{{productName}} moved to clipboard — no room after catalog update',
          undoHint: 'Ctrl+Z to undo',
          // fix-006 — shown only when the product has no hangable option
          // anywhere in its catalog; "Switch" falls back to defineAndHang
          // in that case instead of this being the actual outcome.
          defineHangingSwitchHint:
            'Pick a hangable orientation from Display options to hang it.',
          // fix-006 — drag-time "Switch to a hangable orientation" now
          // resolves to a real option and completes the placement.
          defineHangingSwitched:
            'Switched to {{orientationName}} and hung the product.',
          stackTrimmed: 'Stack reduced to {{count}} to fit the shelf',
          // fix-034 — rung 3 of the stack ladder: the shelf had room, the
          // orientation's own max stack count did not. Distinct wording
          // from `stackTrimmed` so the planner knows which limit to raise.
          stackCapped:
            'Stack reduced to {{count}} — the max stack count for this display option',
          unstacked: 'Stacking removed — selected option is not stackable',
          facingsReduced: 'Facings reduced to {{count}} to fit the shelf',
          facingsDropped: '{{count}} facings removed to fit',
          // fix-046 — a hung set's option change re-lays the set out; when its
          // row cannot hold every product, the end of the run is removed.
          pegProductsRemoved: '{{count}} products removed to fit the pegboard',
          pegNoRoom:
            'Cannot change the display option — there is no room on the pegboard for this orientation.',
          groupsSkipped: '{{count}} groups skipped — target orientation disabled',
          rotatedWithSkips: 'Rotated {{rotated}}, skipped {{skipped}} — orientation disabled.',
          // fix-048 — hung groups whose rotation target has no hanging hole.
          rotatedSkipsNotHangable:
            'Rotated {{rotated}}, skipped {{skipped}} — cannot hang in that orientation.',
          // fix-068 — a single group whose target orientation is taller
          // than its shelf's clearance (FR-20 / T-05 §4).
          rotatedSkipsTooTall:
            'Rotated {{rotated}}, skipped {{skipped}} — orientation too tall for the shelf.',
          // fix-039 — a rotation of several groups is all-or-nothing at
          // full facings; nothing rotated when this shows.
          rotateNoRoomMulti:
            'Cannot rotate — there is not enough room for all the selected groups. Select fewer groups and try again.',
          // fix-039 / FR-20 — a height block names the product: selecting
          // fewer groups only helps if this one is deselected.
          rotateTooTallMulti:
            'Cannot rotate — {{productName}} is too tall for its shelf as {{orientationName}}. Deselect it and try again.',
          rotateBlocked:
            'This rotation would need to bump other products — resolve it from Manage display options.',
          needsConfirm:
            'This change would need to bump other products — resolve it from Manage display options.',
        },
        indicator: {
          nonDefault: 'Placed in {{optionName}}',
          mixed: 'Mixed display options',
        },
        report: {
          orientationColumn: 'Orientation',
        },
        capacity: {
          packs: '{{packs}} packs ({{units}} units)',
          units: '{{units}} units',
        },
        anomaly: {
          stackExceedsLimit: 'Some stacks exceed the shelf limit and need to be reduced',
        },
        dialog: {
          blockedFit: {
            title: 'No room for {{productName}}',
            body: 'There is no room for even one facing. Move to clipboard, or cancel?',
            moveToClipboard: 'Move to clipboard',
            cancel: 'Cancel',
          },
          defineHanging: {
            title: 'How would you like to hang?',
            // The product the question is about — moved out of the title
            // 2026-10-29 so the question is asked bare (see the dialog).
            product:
              '{{productName}} cannot be hung the way it is set up now. Choose how to continue',
            defineAndHang: 'Define hanging for {{orientationName}} and continue',
            switchOrientation: 'Switch to a hangable orientation',
            currentDisabled:
              '{{orientationName}} is disabled for this product, so it cannot be hung in it.',
            cancel: 'Cancel',
          },
          addMode: {
            title: 'Add display mode',
            name: 'Name',
            namePlaceholder: 'e.g. Tray 40 cm',
            width: 'Width',
            height: 'Height',
            depth: 'Depth',
            uom: 'UOM',
            capacity: 'Capacity (units per pack)',
            cancel: 'Cancel',
            create: 'Create',
            created: 'Display mode created',
            nameExists: 'A display option named "{{name}}" already exists.',
            errors: {
              nonNegative: 'Cannot be negative.',
              capacityWhole: 'Must be a whole number greater than 0.',
            },
            nameRequired: 'Name is required',
          },
        },
        flyout: {
          rowLabel: 'Display options',
          manage: 'Manage display options…',
          orientationsTab: 'Orientations',
          rotationTab: 'Rotation',
          currentOption: 'Current',
          rotateInPlace: 'Rotate in place',
          rotateTipSide: 'Tip on its side',
          rotateTipBack: 'Tip backward',
          // FR-16's result preview on the rotation tab — each verb names the
          // orientation it will produce before you commit. `rotateResultLabel`
          // is the static word only; the orientation name is appended as a
          // plain JSX child, never interpolated (see the flyout's own note on
          // i18next escaping + RTL word order).
          rotateResultLabel: 'Result',
          // The unit on the result line's W × H × D triplet. The canvas
          // scene is entirely in cm and ProductContextMenu hands the flyout
          // `wCm/hCm/dCm`, so this is a label, not a conversion.
          cm: 'cm',
          rotateResultMissing:
            'This orientation is not available on this product — the rotation will be skipped',
          rotateResultDisabled:
            'orientation disabled — the rotation will be skipped',
          // fix-048 — a hung product's rotation target with no hanging hole.
          rotateResultNotHangable:
            'Cannot hang in this orientation — the rotation will be skipped',
          rotateResultTooTall:
            'Too tall for this shelf — the rotation will be skipped',
          disabledReason: {
            orientation: 'orientation disabled',
            mode: 'display mode disabled',
            tooTall: 'Too tall for this shelf',
            // fix-074 — a display mode whose Width/Height/Depth/UOM were
            // left blank (or saved as 0) has no footprint and can never be
            // placed. Short on purpose, like `notHangable`: the row already
            // prefixes it with the mode's own name.
            incompleteConfig: 'Missing configuration',
            // Reported 2026-10-13 (second pass): kept SHORT on purpose. The
            // row already prefixes this with the orientation's own name and
            // the action sits on the same row, so the old sentence's
            // "— define hanging to enable it" tail only wrapped the line and
            // made the list run off the bottom of the screen.
            notHangable: 'Cannot hang in this orientation',
          },
          // Short enough to sit INLINE on the row (same report): the reason
          // beside it already says what it is for.
          defineHangingAction: 'Define hanging',
          close: 'Close',
          title: 'Display options',
        },
        excelImport: {
          title: 'Import display options',
          kindLabel: 'File type',
          chooseFile: 'Choose file to upload',
          kind: {
            orientations: 'Orientations file',
            displayModes: 'Display modes file',
          },
          downloadOrientations: 'Download orientations file',
          downloadDisplayModes: 'Download display modes file',
          previewCount: '{{count}} rows ready to upload',
          noFile: 'No file selected yet',
          downloadFailed: 'Download failed rows',
          readError: 'Could not read this file: {{message}}',
          // Reported 2026-10-27 — the wrong-file-type detail that fills
          // `readError`'s {{message}}. One COMPLETE sentence per kind rather
          // than a shared frame with a `{{fileType}}` fragment: the file's
          // name carries a definite article in Hebrew (קובץ המנחים /
          // קובץ מצבי התצוגה) that a fragment slot can't place correctly.
          // `{{actual}}`/`{{expected}}` are plain interpolations, not
          // i18next `count` — the pluralized unit would need 4 en + 8 he
          // variants here (see the plural note under `staleness`), and the
          // module's existing count strings (`previewCount`) don't bother
          // either.
          columnCountMismatch: {
            orientations:
              'This file has {{actual}} columns; the orientations file needs exactly {{expected}}. Make sure you picked the matching file type.',
            displayModes:
              'This file has {{actual}} columns; the display modes file needs exactly {{expected}}. Make sure you picked the matching file type.',
          },
          summary: {
            imported: '{{count}} imported',
            failed: '{{count}} failed',
            liveInstancesAffected:
              '{{count}} live placements affected by dimension changes',
          },
          results: {
            row: 'Row',
            status: 'Status',
            reason: 'Reason',
            ok: 'OK',
            error: 'Error',
          },
          // Reported 2026-10-27 — per-row failure reasons. The BACKEND
          // composes these sentences (display-option-excel-service-impl.ts)
          // and sends them as free English text, so the engine's
          // `translateRowReason` matches the English back to these keys.
          // Keep both sides in step: a reworded backend `throw` needs its
          // pattern updated there, not just its wording changed here.
          reason: {
            invalidOrientationCode: 'Invalid orientation code: {{code}} (must be 1-6).',
            noSuchOrientation:
              'No orientation {{code}} option exists for barcode {{barcode}}.',
            productNotFound: 'Product not found for barcode {{barcode}}.',
            barcodeRequired: 'Barcode is required.',
            modeNameRequired: 'Display mode name is required (barcode {{barcode}}).',
            nameAlreadyExists:
              'A display option with this name already exists for this product (barcode {{barcode}}).',
            cannotDisableDefault: 'Cannot disable the default display option.',
            malformedRow: 'Malformed row: expected an object.',
            unknownError: 'Unknown error.',
          },
          columns: {
            barcode: 'Barcode',
            orientationKey: 'Orientation Code',
            isEnabled: 'Enabled',
            isStackable: 'Stackable',
            maxStackableCount: 'Max Stackable Count',
            isHangable: 'Hangable',
            name: 'Name',
            width: 'Width',
            height: 'Height',
            depth: 'Depth',
            uom: 'UOM',
            capacity: 'Capacity',
          },
          subtitle: 'Select and import display option excel files',
          listTitle: 'Excel display options list',
          removeRow: 'Remove row',
        },
      },
      // -----------------------------------------------------------------
      // Story 3.2 (FR-18/23) — staleness chip. Count text uses i18next's
      // OWN plural mechanism, but NOT the commonly-documented `_one`/
      // `_two`/`_few`/`_many`/`_other` CLDR suffix names — this app's
      // i18next 19.9 instance has no `compatibilityJSON: 'v4'` set (a
      // global config change out of this story's scope, and risky for
      // the app's hundreds of existing `_plural`-suffixed keys), so its
      // PluralResolver falls through to bare numeric-index suffixes.
      // Verified by direct runtime test (`pluralResolver.getSuffix('he',
      // n)` for n in 0..200): en is the familiar `` (count===1) /
      // `_plural` (otherwise); he is `_0` (=1, "one") / `_1` (=2, "two") /
      // `_2` (round tens from 20 up, "many") / `_3` (everything else,
      // "other") — the SAME CLDR Hebrew categories, just numeric names.
      // -----------------------------------------------------------------
        ...STALENESS_TRANSLATIONS.en.simulationV3,
      // Story 3.8 (AC 5, FR-61, T-12 resolution 2026-08-07) — the header's
      // unsaved-edits badge (SimulationScreen.tsx's UnsavedIndicator). A
      // dedicated, v3-scoped key, deliberately NOT the shared app-wide
      // `YOU_HAVE_UNSAVED_CHANGES` string (which stays "You have unsaved
      // changes" everywhere else it's used) — shortened here specifically
      // so it reads unambiguously next to the staleness chip's "N pending
      // changes" vocabulary. See changes/2026-08-07-t12-naming-resolution.md.
      unsavedChangesBadge: 'Unsaved changes',
    },
  },
  he: {
    simulationV3: {
      productDetails: {
        catalogImportName: 'השם מייבוא הקטלוג',
        productBrand: 'מותג המוצר',
        unitOfMeasure: 'יחידת מידה',
        centimeters: 'סנטימטרים',
        millimeters: 'מילימטרים',
        inches: 'אינצ׳ים',
        sensitivity: 'רגישות',
        hangable: 'הגדרות תלייה',
        isHangable: 'ניתן לתלייה',
        horizontal: 'אופקי',
        vertical: 'אנכי',
        enablePriceTag: 'הצג תווית מחיר',
        internalCode: 'קוד פנימי',
        image: 'תמונה',
        dropzone: 'גרור ושחרר קבצים לכאן, או לחץ לבחירת קבצים',
        sourceDetails: 'פרטי מקור',
        isMandatory: 'חובה',
        isStackable: 'ניתן לערימה',
        isBlock: 'בלוק',
        mvpOverride: 'עקיפת MVP/עליון',
        minRevenue: 'השתמש בהכנסה מזערית ב',
        gs1Override: 'עקיפת GS1',
        archive: 'לארכיון',
        rerunToRefresh:
          'המוצר הוסר מנתוני האב. יש להריץ את הסימולציה מחדש לריענון הפריסה.',
        imageTypeRejected: 'יש לבחור תמונת PNG, JPG, WEBP או AVIF.',
        imageTooLarge: 'התמונה גדולה מ-5MB.',
        maxStackCount: 'מספר שכבות מקסימלי',
        archiveConfirm: 'להעביר מוצר זה לארכיון?',
        maxStackCountHint: 'השאר ריק או 0 כדי לא להגביל את מספר השכבות',
        archivedToast: 'המוצר {{name}} הועבר לארכיון בהצלחה.',
        restoredToast: 'המוצר {{name}} שוחזר בהצלחה.',
      },
      screenTitle: 'סימולציה',
      screenTitleOf: 'של',
      dev: {
        loadSample: 'טען נתוני דוגמה (פיתוח)',
        load4Field: 'טען דוגמה של 4 שדות (פיתוח)',
        load50Field: 'טען דוגמה של 50 שדות (פיתוח)',
      },
      /* CR010 — the "color by" control, its list and the tint slider. */
      colorBy: {
        title: 'צבע לפי',
        none: 'ללא',
        tint: 'שקיפות גוון',
        dim: {
          brand: 'מותג',
          supplier: 'ספק',
          subCategory: 'תת קטגוריה',
          category: 'קטגוריה',
        },
      },
      toolbar: {
        tools: 'כלים',
        zoomIn: 'הגדל תצוגה',
        zoomOut: 'הקטן תצוגה',
        zoomReset: 'אפס תצוגה',
        pan: 'ניווט',
        xray: 'שקיפות',
        fullscreen: 'מסך מלא',
        exitFullscreen: 'צא ממסך מלא',
        minimize: 'מזער מסך',
        undo: 'בטל',
        redo: 'בצע מחדש',
        viewStackables: 'הצג מוצרים ניתנים לערימה',
        hideStackables: 'הסתר מוצרים ניתנים לערימה',
        dropToPegboard: 'שחרר על לוח התלייה',
        dropToShelf: 'שחרר על המדף',
        duEdit: 'עריכת יחידות תצוגה',
        iconLegend: 'מקרא סמלים',
      },
      errors: {
        loadFailed: 'טעינת הסימולציה נכשלה. נסה שוב.',
        saveFailed: 'שמירת הסימולציה נכשלה. נסה שוב.',
        clustersFailed: 'טעינת המקבצים נכשלה.',
        ackWarningFailed: 'הסרת ההתראה נכשלה. נסה שוב.',
        refreshFailed: 'לא ניתן היה להתחיל את הרענון. נסה שוב.',
      },
      clipboard: {
        title: 'לוח גזירים',
        removed: 'הוסרו',
        added: 'נוספו',
        unknownProduct: 'מוצר לא ידוע',
        empty: 'לוח הגזירים ריק',
        clearAll: 'נקה הכול',
        clearSelection: 'נקה בחירה',
        singleDrag: 'גרירת קבוצה בודדת',
        multiDrag: 'גרירת כל הקבוצות',
        discard: 'הסר מלוח הגזירים',
        discardSingle: 'הפריט יוסר מלוח הגזירים לאחר שמירת הפלנוגרמה. להמשיך?',
        discardSelected:
          'המוצרים שנבחרו יוסרו מלוח הגזירים לאחר שמירת הפלנוגרמה. להמשיך?',
        discardAll: 'כל לוח הגזירים ינוקה. להמשיך?',
      },
      dataPanel: {
        searchPlaceholder: 'חפש כאן…',
        chooseColumns: 'בחר עד 3 עמודות',
        cancel: 'ביטול',
        confirm: 'אישור',
        recentlyAdded: 'נוספו לאחרונה',
        allCategories: 'כל הקטגוריות',
        allSuppliers: 'כל הספקים',
        weighted: 'משוקלל',
        allocated: 'מוקצה',
        shareOfSale: 'נתח מכירות',
        salesUnits: 'יחידות',
        dailyRate: 'קצב יומי',
        byFace: 'לפי פייסים',
        hangable: 'ניתן לתלייה / לא ניתן לתלייה',
        hangableOnly: 'ניתן לתלייה',
        nonHangableOnly: 'לא ניתן לתלייה',
        tolerance: 'סטייה',
        share: 'נתח',
        saveAndPrintPrompt: 'השינויים האחרונים לא נשמרו. להדפיס בתום השמירה?',
      },
      contextMenu: {
        facings: 'פייסים',
        stack: 'ערימה',
        apply: 'החל',
        close: 'סגור',
        expand: 'הרחב',
      },
      copyArrangement: {
        blocked: 'לסימולציה שנבחרה מבנה שדות שונה — לא ניתן להעתיק',
        failed: 'העתקת הסידור נכשלה',
        shelfMismatch:
          'מבנה המדפים שונה מהפריסה הנוכחית. להמשיך ולהעתיק בכל זאת?',
        empty: 'לא נמצאו סימולציות מתאימות',
        editionsEmpty: 'לא נמצאו מהדורות',
        searching: 'מחפש סימולציות…',
        applying: 'מעתיק את הסידור…',
        mismatchTitle: 'אי-התאמה במבנה',
        continue: 'המשך',
        sessionLost:
          'הסימולציה השתנתה במהלך העתקת הסידור — דבר לא הועתק. נסה שוב.',
        filterTitle: 'מסנן העתקת סידור',
        cluster: 'מקבץ',
        search: 'חפש',
        fieldsWidth: 'רוחב שדות',
        planogramContains: 'פלנוגרמה שמכילה…',
      },
      filter: {
        cluster: 'מקבץ',
        selectCluster: 'בחר מקבץ…',
        branch: 'סניף',
        selectBranch: 'בחר סניף',
        clusterLevel: 'רמת מקבץ',
        tags: 'תגיות',
        selectTags: 'בחר תגיות',
        strategy: 'אסטרטגיה',
        edition: 'מהדורה',
        fieldCount: 'שדות',
        defaultCount: 'ברירת מחדל',
        simulate: 'צור פלנוגרמה',
        simulating: 'מייצר פלנוגרמה…',
        save: 'שמור',
        saving: 'שומר…',
        pendingFeature: 'בקרוב בגרסה המחודשת',
        customBranchInfo:
          'במצב מותאם אישית, בחירת הסניף משמשת להורדת הסימולציה בלבד',
        fieldArrayMismatch:
          'הסימולציה השמורה נבנתה על מערך שדות שונה מזה שנבחר. בדוק לפני עריכה.',
      },
      tabs: {
        preview: 'תצוגה מקדימה',
        summary: 'סיכום',
      },
      panel: {
        emptyList: 'אין פריטים עדיין',
        pendingBackend: 'יהיה זמין בעדכון הבא.',
        dimensionChanged: 'מידות המוצר השתנו מאז יצירת הסימולציה.',
        filters: 'מסננים',
        listView: 'תצוגת רשימה',
        gridView: 'תצוגת רשת',
        copyBarcode: 'לחץ להעתקת הברקוד',
      },
      /* CR011: איתור דו-כיווני (דאבל-קליק) — הנוסח שאושר במפרט. */
      locate: {
        notPlaced: 'המוצר אינו מוצב בפלנוגרמה',
        notInPanel: 'המוצר אינו מוצג בלוח הנתונים בסינון הנוכחי',
        rowTooltip: 'דאבל-קליק לאיתור על הקנבס',
        duCardTooltip: 'קליק לכרטיס המוצר, לחיצה כפולה לאיתור בפלנוגרמה',
      },
      stackable: {
        /**
         * Hebrew plural forms — i18next v19 emits INDEX suffixes for `he`
         * (`_0` = 1, `_1` = 2, `_2` = tens, `_3` = the rest), not the
         * `_one`/`_other` names of v21+. Verified against the installed
         * resolver. The unsuffixed key stays as the safety net.
         */
        marked: '{{count}} מוצרים סומנו כניתנים לערימה.',
        marked_0: 'מוצר אחד סומן כניתן לערימה.',
        marked_1: 'שני מוצרים סומנו כניתנים לערימה.',
        marked_2: '{{count}} מוצרים סומנו כניתנים לערימה.',
        marked_3: '{{count}} מוצרים סומנו כניתנים לערימה.',
        cleared: 'סימון הערימה בוטל עבור {{count}} מוצרים.',
        cleared_0: 'סימון הערימה בוטל עבור מוצר אחד.',
        cleared_1: 'סימון הערימה בוטל עבור שני מוצרים.',
        cleared_2: 'סימון הערימה בוטל עבור {{count}} מוצרים.',
        cleared_3: 'סימון הערימה בוטל עבור {{count}} מוצרים.',
        allEmpty: 'אין מוצרים במשטח העבודה לסימון.',
        failed: 'עדכון המוצרים נכשל — השינוי לא בוצע.',
      },
      pegboard: {
        drawHint: 'צייר מלבן על משטח העבודה כדי למקם את לוח התלייה.',
        cancel: 'ביטול',
        deleteMovedToClipboard:
          'לוח התלייה נמחק. {{count}} מוצרים תלויים הועברו ללוח הגזירים.',
        deleteMovedToClipboard_0:
          'לוח התלייה נמחק. מוצר תלוי אחד הועבר ללוח הגזירים.',
        deleteMovedToClipboard_1:
          'לוח התלייה נמחק. שני מוצרים תלויים הועברו ללוח הגזירים.',
        deleteMovedToClipboard_2:
          'לוח התלייה נמחק. {{count}} מוצרים תלויים הועברו ללוח הגזירים.',
        deleteMovedToClipboard_3:
          'לוח התלייה נמחק. {{count}} מוצרים תלויים הועברו ללוח הגזירים.',
        updateTitle: 'לעדכן את לוח התלייה?',
        updateMovesToClipboard:
          'לוח תלייה זה מכיל מוצרים תלויים. עדכונו יעביר את כל המוצרים התלויים ללוח הגזירים. להמשיך?',
        updateCancel: 'ביטול',
        updateConfirm: 'כן, עדכן',
        deleteTitle: 'למחוק את לוח התלייה?',
        deleteMessage:
          'לוח תלייה זה מכיל מוצרים תלויים. מחיקתו תעביר את כל המוצרים התלויים ללוח הגזירים. להמשיך?',
        deleteCancel: 'ביטול',
        deleteConfirm: 'כן, מחק',
      },
      dropDisplace: {
        title: 'אין מספיק מקום',
        message:
          'אין מקום לדחוק את המוצרים הצידה במדף זה. להעביר את המוצרים הבאים ללוח הגזירים ולשבץ את המוצר החדש במקומם?',
        confirm: 'העבר ללוח הגזירים ושבץ',
        cancel: 'ביטול',
      },
      export: {
        button: 'ייצוא',
        pdfFull: 'PDF — פלנוגרמה מלאה',
        pdfFieldwise: 'PDF — שדה אחר שדה',
        excelFieldwise: 'אקסל — שדה אחר שדה',
        cover: 'בונה עמוד שער…',
        capturing: 'מעבד את הפלנוגרמה…',
        excelProducts: 'אקסל — רשימת מוצרים',
        preparing: 'מכין ייצוא…',
        collecting: 'אוסף נתונים…',
        writing: 'כותב קובץ…',
        done: 'הושלם',
        failed: 'הייצוא נכשל. נסה שוב.',
        unsavedPrompt: 'יש שינויים שלא נשמרו. לשמור לפני הייצוא?',
        configureColumns: 'הגדרת עמודות אקסל',
        saveAndExport: 'שמור וייצא',
        fieldPlanogram: 'פלנוגרמת שדה',
        fieldWisePrint: 'הדפסה לפי שדה',
        categoryWisePrint: 'הדפסה לפי קטגוריה',
        facing: 'פייס',
        stackCount: 'מספר שכבות',
      },
      addProduct: {
        title: 'הוספת מוצרים',
        added: 'נוסף',
      },
      duEdit: {
        hint: 'בחר יחידת תצוגה לעריכה או הוסף חדשה',
        title: 'יחידות תצוגה',
        shelvesTab: 'מדפים',
        pickShelf: 'בחר מדף כדי להתחיל לערוך',
        close: 'סגור',
        addShelf: '+ מדף',
        removeShelf: '− מדף',
        removeBlocked: 'לא ניתן להסיר מדף שיש עליו מוצרים.',
        movedToClipboard:
          'מוצרים שנותרו ללא תמיכת מדף מלאה הועברו ללוח הגזירים.',
        unsavedTitle: 'שינויים שלא נשמרו',
        unsavedMessage: 'יש שינויים שלא נשמרו. לצאת בכל זאת?',
        unsavedBack: 'חזור',
        unsavedLeave: 'כן',
        deleteShelfTitle: 'אישור מחיקת מדף',
        deleteShelfMessage:
          'יש {{count}} מוצרים על המדף. להעביר אותם ללוח הגזירים?',
        deleteShelfMessage_0: 'יש מוצר אחד על המדף. להעביר אותו ללוח הגזירים?',
        deleteShelfMessage_1:
          'יש שני מוצרים על המדף. להעביר אותם ללוח הגזירים?',
        deleteShelfMessage_2:
          'יש {{count}} מוצרים על המדף. להעביר אותם ללוח הגזירים?',
        deleteShelfMessage_3:
          'יש {{count}} מוצרים על המדף. להעביר אותם ללוח הגזירים?',
        deleteShelfBack: 'חזור',
        deleteShelfConfirm: 'כן',
        shelfTitle: 'מדף : S{{rank}}',
        space: 'שטח מדף',
        fromFloor: 'מהרצפה',
        thickness: 'עובי',
        addAbove: '+ מעל',
        addBelow: '+ מתחת',
        overflowWarning: 'אזהרת חריגת מוצרים',
      },
      modal: {
        saveTitle: 'שמירת שינויים לפלנוגרמה בודדת',
        saveMessage: 'השינויים שביצעת יישמרו רק לסימולציה הנוכחית. לשמור?',
        unsavedTitle: 'שינויים שלא נשמרו',
        discard: 'בטל',
        saveAndContinue: 'שמור והמשך',
        resetAndContinue: 'אפס והמשך',
        arrangeTitle: 'בחירת יחידת תצוגה לסידור מחדש',
        arrangeMessage:
          'להשתמש ביחידת התצוגה הנוכחית שנערכה או לאפס למערך השדות של המקבץ שנבחר?',
        useCurrentDU: 'השתמש ביחידת תצוגה נוכחית',
        resetFieldArray: 'אפס למערך שדות שנבחר',
        closeTitle: 'לסגור את הסימולציה?',
      },
      rules: {
        categoryOverlap: 'אזורי הקטגוריות חופפים',
        ruleExceeded: 'חריגה מכלל תת-קטגוריה',
        field: 'שדה',
        none: 'אין הפרות כללים.',
      },
      warnings: {
        overlap: 'המוצר חופף למוצר אחר.',
        hungOverlap:
          'המוצר — או אחת משכבות הערימה — פוגע במוצר התלוי על לוח התלייה מעליו.',
        pegOverlap: 'המוצר התלוי חופף למוצר אחר.',
        boardArea:
          'המיקום נמצא בתוך שטח לוח התלייה — אין מקום למוצר מתחת ללוח.',
        noFreeHole:
          'אין חור פנוי במיקום זה בלוח התלייה. נסה מקום עם יותר שטח פנוי.',
        pegGridUnsaved: 'יש לשמור את ערכי לוח התלייה לפני הצבת מוצרים עליו.',
        heightExceedsShelf: 'המוצר גבוה מהמותר במדף.',
        heightExceedsShelfDetail:
          'המוצר לא נכנס למדף זה: „{{name}}” בגובה {{height}} ס״מ, אך במדף יש רק {{space}} ס״מ גובה.',
        heightExceedsShelfSet:
          '{{count}} מוצרים בסט גבוהים מדי למדף זה. „{{name}}” בגובה {{height}} ס״מ, אך במדף יש רק {{space}} ס״מ גובה.',
        maxStackExceeded:
          'מספר השכבות חורג ממספר השכבות המקסימלי של המוצר ({{max}}). יש להזין ערך של {{max}} או פחות.',
        orientationNotStackable:
          'המוצר אינו ניתן לערימה באפשרות התצוגה הנוכחית שלו.',
        mixedMove: 'לא ניתן להזיז יחד מוצרי מדף ומוצרים תלויים.',
        mixedClipboard:
          'לא ניתן לגזור, להעתיק או למחוק יחד מוצרי מדף ומוצרים תלויים.',
        noRoomToExpand: 'אין מקום להוסיף פייסים באף צד.',
        pegboardOverlap: 'המוצר התלוי חורג מלוח התלייה ומתנגש במדפים.',
        noSpace: 'אין מקום פנוי במיקום היעד.',
        dropNotEnoughSpace:
          'לא ניתן למקם את המוצר כאן: אין מספיק מקום במדף, ולא ניתן להזיז הצידה את המוצרים הקיימים כדי לפנות מקום.',
        dropLongGroupMiddle:
          'קבוצת המוצרים ארוכה. יש למקם את המוצר ליד 2 המוצרים הראשונים או 2 המוצרים האחרונים שלה.',
        setPartialPaste:
          'יש כאן מקום ל-{{placed}} מוצרים בלבד מתוך {{total}} שבסט. סט מוחזר במלואו.',
        setNoSpace: 'אין מקום פנוי לסט המוצרים שנגרר.',
        setCannotPlace: 'לא ניתן למקם כאן את סט המוצרים: {{reason}}',
        hungMembersKept:
          '{{count}} מוצרים בסט היו תלויים על לוח תלייה, ולכן הם נשארים בלוח הגזירה — יש לגרור אותם ללוח כדי להחזירם.',
        hungSetNeedsBoard:
          'כל המוצרים בסט זה היו תלויים על לוח תלייה. יש לשחרר את הסט על לוח כדי להחזירם.',
        notSameRow:
          'המדף אינו באותה שורה כמו המוצרים המועברים. יש להניח אותם על מדף בגובה זהה.',
        outsideField: 'המוצר שוחרר מחוץ לשדה.',
        ruleViolation: 'שיבוץ זה מפר כלל קטגוריה.',
        takeBackDisabled: 'החזרה (Take-back) מושבתת.',
        duEditBlocked: 'לא ניתן לערוך מדף שמכוסה בלוח תלייה.',
        dropConfirmation: 'אשר את שיבוץ המוצר.',
        dimensionsChanged: 'מידות המוצר השתנו מאז רישום הקטלוג.',
        notHangable: 'על לוח תלייה ניתן למקם רק מוצרים המוגדרים לתלייה.',
      },
      // Client-reviewed and FINAL as of 2026-10-19 (approved by Tzvi;
      // "Display-Options-Hebrew - FINAL 19.10.xlsx", 147 keys). This replaces
      // the 2026-09-10 AI-drafted first pass, which had never been through
      // the module's client glossary/style review.
      //
      // Glossary decisions from that review, applied throughout this subtree:
      //   orientation            = מנח  (plural מנחים) — NOT כיוון/כיוונים
      //   the six orientations   = חזית / חזית בשכיבה / צד / צד בשכיבה /
      //                            עליון / עליון בסיבוב
      //   max stack count        = כמות מרבית לערימה
      //
      // Three keys postdate that review and are still first-draft Hebrew:
      // `detail.maxStackableCountHint` and the two `dialog.addMode.errors.*`.
      // Send them with the next review round.
      displayOptions: {
        banner: 'אתה עורך את הקטלוג — השינויים חלים מיד על כל הפלנוגרמות.',
        loading: 'טוען…',
        header: {
          barcodeLabel: 'ברקוד',
        },
        orientation: {
          front: 'חזית',
          frontLaydown: 'חזית בשכיבה',
          side: 'צד',
          sideLaydown: 'צד בשכיבה',
          top: 'עליון',
          topRotated: 'עליון בסיבוב',
        },
        list: {
          orientations: 'מנחים',
          displayModes: 'מצבי תצוגה',
          orientationsHint: 'אוטומטי',
          displayModesHint: 'ידני',
          noModes: 'אין עדיין מצבי תצוגה',
          addMode: '+ הוספת מצב תצוגה',
          default: 'ברירת מחדל',
          archived: 'בארכיון',
          disabled: 'מושבת',
          disabledTag: 'מושבת',
          capacity: 'קיבולת {{units}}',
          orientationLocked: 'לא ניתן להעביר מנחים לארכיון או למחוק אותם',
          orientationLockedDisabled: 'מושבת — לא ניתן להעביר מנחים לארכיון או למחוק אותם',
        },
        preview: {
          banner: 'ששת המנחים האלה נוצרים אוטומטית כשתשמור את המוצר. אפשר להגדיר אותם אחר כך.',
          detailHint: 'שמור את המוצר כדי להגדיר לכל אפשרות מידות, ערימה, תלייה ותמונה.',
        },
        detail: {
          empty: 'בחר אפשרות כדי להציג פרטים',
          name: 'שם',
          width: 'רוחב',
          height: 'גובה',
          depth: 'עומק',
          uom: "יח' מידה",
          capacity: 'קיבולת (יחידות באריזה)',
          calculated: 'מחושב',
          stackable: 'ניתן לערימה',
          maxStackableCount: 'כמות מרבית לערימה',
          maxStackableCountHint: 'השאר ריק או 0 אם אינך רוצה להגביל את מספר הערימה',
          hangable: 'ניתן לתלייה',
          hangHoleX: 'אופקי',
          hangHoleY: 'אנכי',
          /* fix-082 follow-up — the pair of Capacity's
             "הערך חייב להיות מספר שלם גדול מ-0.", without "שלם" (whole):
             a hang hole may legitimately be 16.5 or 0.5. */
          hangHolePositive: 'הערך חייב להיות גדול מ-0.',
          yes: 'כן',
          no: 'לא',
          update: 'עדכן',
          updated: 'עודכן',
          setDefault: 'קבע כברירת מחדל',
          enable: 'הפעל',
          disable: 'השבת',
          archive: 'העבר לארכיון',
          delete: 'מחק',
          lockDefaultDisable: 'לא ניתן להשבית את אפשרות ברירת המחדל',
          lockArchivedToggle: 'לא ניתן לשנות את מצב ההפעלה של אפשרות שבארכיון',
          lockDefaultArchive: 'לא ניתן להעביר את אפשרות ברירת המחדל לארכיון',
          lockDefaultDelete:
            'לא ניתן למחוק את אפשרות ברירת המחדל — קבע תחילה אפשרות אחרת כברירת מחדל',
          lockOrientationArchive: 'לא ניתן להעביר מנחים לארכיון — השתמש בהשבתה',
          lockSetDefault: 'לא ניתן לקבוע אפשרות מושבתת או בארכיון כברירת מחדל',
        },
        image: {
          label: 'תמונה',
          readOnly: 'העלאת תמונה אינה זמינה',
          pending: 'התמונה תועלה כשתשמור',
        },
        affectedInstances: {
          title: 'פעולה זו משפיעה על שיבוצים פעילים',
          body: 'פעולה זו משנה את {{name}} בקטלוג. שיבוץ פעיל אחד יתעדכן מיידית — לא ניתן לבטל פעולה זו.',
          body_plural:
            'פעולה זו משנה את {{name}} בקטלוג. {{count}} שיבוצים פעילים יתעדכנו מיידית — לא ניתן לבטל פעולה זו.',
          ok: 'הבנתי',
        },
        toast: {
          needsEvict:
            'שינוי זה ידרוש להעביר מוצרים אחרים ללוח הגזירים — טפל בכך ב"ניהול אפשרויות תצוגה".',
          catalogEvicted:
            '{{productName}} הועבר ללוח הגזירים — אין מקום לאחר עדכון הקטלוג.',
          undoHint: 'Ctrl+Z לביטול',
          defineHangingSwitchHint: 'בחר מנח הניתן לתלייה ב"אפשרויות תצוגה" כדי לתלות אותו.',
          defineHangingSwitched: 'המוצר הועבר למנח {{orientationName}} ונתלה.',
          stackTrimmed: 'הערימה הוקטנה ל-{{count}} כדי להתאים למדף',
          stackCapped:
            'הערימה הוקטנה ל-{{count}} — כמות השכבות המרבית לאפשרות תצוגה זו',
          unstacked: 'הערימה בוטלה — האפשרות שנבחרה אינה ניתנת לערימה',
          facingsReduced: 'מספר הפייסים הוקטן ל-{{count}} כדי להתאים למדף',
          facingsDropped: '{{count}} פייסים הוסרו כדי להתאים למדף',
          pegProductsRemoved: '{{count}} מוצרים הוסרו כדי להתאים ללוח התלייה',
          pegNoRoom:
            'לא ניתן לשנות את אפשרות התצוגה — אין מקום בלוח התלייה למנח זה.',
          groupsSkipped: '{{count}} קבוצות דולגו — המנח המבוקש מושבת',
          rotatedWithSkips: 'סובבו {{rotated}}, דולגו {{skipped}} — המנח מושבת.',
          rotatedSkipsNotHangable:
            'סובבו {{rotated}}, דולגו {{skipped}} — לא ניתן לתלות במנח זה.',
          rotatedSkipsTooTall:
            'סובבו {{rotated}}, דולגו {{skipped}} — המנח גבוה מדי עבור המדף.',
          rotateNoRoomMulti:
            'לא ניתן לסובב — אין מספיק מקום לכל הקבוצות שנבחרו. בחר פחות קבוצות ונסה שוב.',
          rotateTooTallMulti:
            'לא ניתן לסובב — {{productName}} גבוה מדי למדף שלו במנח {{orientationName}}. בטל את בחירתו ונסה שוב.',
          rotateBlocked: 'סיבוב זה ידרוש לדחוק מוצרים אחרים — טפל בכך ב"ניהול אפשרויות תצוגה".',
          needsConfirm: 'שינוי זה ידרוש לדחוק מוצרים אחרים — טפל בכך ב"ניהול אפשרויות תצוגה".',
        },
        indicator: {
          nonDefault: 'מוצב: {{optionName}}',
          mixed: 'אפשרויות תצוגה מעורבות',
        },
        report: {
          orientationColumn: 'מנח',
        },
        capacity: {
          packs: '{{packs}} אריזות ({{units}} יחידות)',
          units: '{{units}} יחידות',
        },
        anomaly: {
          stackExceedsLimit: 'יש ערימות גבוהות מהמדף — צריך להקטין אותן',
        },
        dialog: {
          blockedFit: {
            title: 'אין מקום עבור {{productName}}',
            body: 'אין מקום אפילו לפייס אחד. להעביר ללוח הגזירים, או לבטל?',
            moveToClipboard: 'העבר ללוח הגזירים',
            cancel: 'ביטול',
          },
          defineHanging: {
            title: 'איך לתלות?',
            product:
              'לא ניתן לתלות את {{productName}} כפי שהוא מוגדר כעת. בחר כיצד להמשיך',
            defineAndHang: 'הגדר תלייה למנח {{orientationName}} והמשך',
            switchOrientation: 'עבור למנח הניתן לתלייה',
            currentDisabled: 'המנח {{orientationName}} מושבת עבור מוצר זה, ולכן לא ניתן לתלות בו.',
            cancel: 'ביטול',
          },
          addMode: {
            title: 'הוספת מצב תצוגה',
            name: 'שם',
            namePlaceholder: 'למשל: מגש 40 ס״מ',
            width: 'רוחב',
            height: 'גובה',
            depth: 'עומק',
            uom: "יח' מידה",
            capacity: 'קיבולת (יחידות באריזה)',
            cancel: 'ביטול',
            create: 'צור',
            created: 'מצב התצוגה נוצר',
            nameExists: 'כבר קיימת אפשרות תצוגה בשם "{{name}}".',
            errors: {
              nonNegative: 'הערך לא יכול להיות שלילי.',
              capacityWhole: 'הערך חייב להיות מספר שלם גדול מ-0.',
            },
            nameRequired: 'חובה למלא שם',
          },
        },
        flyout: {
          rowLabel: 'אפשרויות תצוגה',
          manage: 'ניהול אפשרויות תצוגה…',
          orientationsTab: 'מנחים',
          rotationTab: 'סיבוב',
          currentOption: 'נוכחי',
          rotateInPlace: 'סובב במקום',
          rotateTipSide: 'הטה על הצד',
          rotateTipBack: 'הטה אחורה',
          rotateResultLabel: 'תוצאה',
          cm: 'ס״מ',
          rotateResultMissing: 'המנח הזה לא קיים במוצר — הסיבוב ידולג',
          rotateResultDisabled:
            '— המנח מושבת, הסיבוב ידולג',
          rotateResultNotHangable: 'לא ניתן לתלות במנח זה — הסיבוב ידולג',
          rotateResultTooTall: 'גבוה מדי עבור מדף זה — הסיבוב ידולג',
          disabledReason: {
            orientation: '— המנח מושבת',
            mode: '— מצב התצוגה מושבת',
            tooTall: 'גבוה מדי עבור מדף זה',
            incompleteConfig: 'הגדרות חסרות',
            notHangable: 'לא ניתן לתלות במנח זה',
          },
          defineHangingAction: 'הגדר תלייה',
          close: 'סגור',
          title: 'אפשרויות תצוגה',
        },
        excelImport: {
          title: 'ייבוא אפשרויות תצוגה',
          kindLabel: 'סוג קובץ',
          chooseFile: 'בחר קובץ להעלאה',
          kind: {
            orientations: 'קובץ מנחים',
            displayModes: 'קובץ מצבי תצוגה',
          },
          downloadOrientations: 'הורד קובץ מנחים',
          downloadDisplayModes: 'הורד קובץ מצבי תצוגה',
          previewCount: '{{count}} שורות מוכנות להעלאה',
          noFile: 'לא נבחר קובץ עדיין',
          downloadFailed: 'הורד שורות שנכשלו',
          readError: 'לא ניתן לקרוא את הקובץ: {{message}}',
          columnCountMismatch: {
            orientations:
              'בקובץ זה {{actual}} עמודות; בקובץ המנחים נדרשות בדיוק {{expected}} עמודות. ודא שבחרת את סוג הקובץ המתאים.',
            displayModes:
              'בקובץ זה {{actual}} עמודות; בקובץ מצבי התצוגה נדרשות בדיוק {{expected}} עמודות. ודא שבחרת את סוג הקובץ המתאים.',
          },
          summary: {
            imported: '{{count}} יובאו',
            failed: '{{count}} נכשלו',
            liveInstancesAffected: '{{count}} שיבוצים פעילים מושפעים משינוי המידות',
          },
          results: {
            row: 'שורה',
            status: 'סטטוס',
            reason: 'סיבה',
            ok: 'תקין',
            error: 'שגיאה',
          },
          reason: {
            invalidOrientationCode: 'קוד מנח לא חוקי: {{code}} (חייב להיות 1-6).',
            noSuchOrientation: 'לא קיים מנח {{code}} עבור הברקוד {{barcode}}.',
            productNotFound: 'לא נמצא מוצר עבור הברקוד {{barcode}}.',
            barcodeRequired: 'ברקוד הוא שדה חובה.',
            modeNameRequired: 'שם מצב התצוגה הוא שדה חובה (ברקוד {{barcode}}).',
            nameAlreadyExists:
              'כבר קיימת אפשרות תצוגה בשם זה עבור מוצר זה (ברקוד {{barcode}}).',
            cannotDisableDefault: 'לא ניתן להשבית את אפשרות התצוגה שהיא ברירת המחדל.',
            malformedRow: 'שורה פגומה: נדרש אובייקט.',
            unknownError: 'שגיאה לא ידועה.',
          },
          columns: {
            barcode: 'ברקוד',
            orientationKey: 'קוד מנח',
            isEnabled: 'מופעל',
            isStackable: 'ניתן לערימה',
            maxStackableCount: 'כמות מרבית לערימה',
            isHangable: 'ניתן לתלייה',
            name: 'שם',
            width: 'רוחב',
            height: 'גובה',
            depth: 'עומק',
            uom: "יח' מידה",
            capacity: 'קיבולת',
          },
          subtitle: 'בחר קובצי אקסל של אפשרויות תצוגה וייבא אותם',
          listTitle: 'רשימת אפשרויות התצוגה מהאקסל',
          removeRow: 'הסר שורה',
        },
      },
        ...STALENESS_TRANSLATIONS.he.simulationV3,
      unsavedChangesBadge: 'שינויים שלא נשמרו',
    },
  },
}

/**
 * Merge the bundle into i18next. Deep + overwrite so it survives a
 * bundle that already carries older `simulationV3` keys, and idempotent
 * — the module entry calls it once at load, before the first render.
 */
interface ResourceHost {
  addResourceBundle?: (
    language: string,
    namespace: string,
    bundle: unknown,
    deep: boolean,
    overwrite: boolean
  ) => unknown
}

export const registerSimulationV3Translations = (
  /** Injectable for tests; production always uses the app instance. */
  target: ResourceHost = i18n as ResourceHost
): void => {
  // Best-effort: hosts and unit tests routinely hand this module a bare
  // i18next stub (`{ t }`). Registration must never be the reason the
  // screen fails to load — without it the keys simply stay untranslated.
  if (typeof target?.addResourceBundle !== 'function') return
  Object.entries(SIMULATION_V3_TRANSLATIONS).forEach(([language, bundle]) => {
    target.addResourceBundle?.(language, 'translation', bundle, true, true)
  })
}
